import { IconButton, Tooltip } from "@kaltura/ds-react-components";
import { Chat24Icon, Clear24Icon } from "@kaltura/ds-react-icons";
import { styled } from "@mui/material";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useEmbeddedCncWidgetContext } from "@kaltura/mediaspace-shared-contexts/EmbeddedCncWidgetContext";

/**
 * Shared placement for both states of the toggle - expanding and collapsing happen in the same
 * spot and differ only in their DS variant, so colors come from the `variant`/`color` props below.
 */
const StyledFloatingToggle = styled(IconButton)(({ theme }) => ({
    // Rendered inside the sidebar layout's floated region, which already provides
    // a fixed, sidebar-aware positioning context - no need to fix-position here too.
    // The floated region's bottom edge already sits above the CnC navigation bar when the
    // buttons rail is visible, so a plain spacing offset lands the button just above the bar.
    position: "absolute",
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: theme.zIndex.drawer + 1,
    boxShadow: theme.kaltura.elevations.medium,
}));

/**
 * Toggle button for expanding/collapsing the CnC sidebar rail.
 * This button is specifically designed for WCAG 2.1 SC 1.4.10 Reflow compliance.
 *
 * The button only appears when:
 * - The viewport width is <= 320px (equivalent to 400% zoom on a 1280px desktop screen)
 * - The device is a desktop with a mouse/trackpad (hover: hover, pointer: fine)
 *
 * This ensures mobile devices are not affected, as they use different input methods
 * (touch/coarse pointer) and have their own responsive layouts.
 *
 * When the sidebar is collapsed, shows an expand button at the bottom-right.
 * When the sidebar is expanded, shows a collapse button above the CnC navigation bar.
 *
 * Both states are the same button, so toggling only swaps its props and keeps the DOM node -
 * and with it the keyboard user's focus.
 */
export const SidebarRailToggle = () => {
    const { isRailCollapsed, setIsRailCollapsed, isHighZoomDesktop } = useEmbeddedCncWidgetContext();

    // This affordance only exists to satisfy reflow - there is nothing to render outside the
    // breakpoint.
    if (!isHighZoomDesktop) {
        return null;
    }

    // Collapsing the rail also closes an open contents drawer, and opening the drawer from outside
    // the rail brings the rail back - both live in SidebarStateManager, which owns the drawer state
    // together with its persistence.
    const label = isRailCollapsed ? translate("Expand engagement panel") : translate("Collapse engagement panel");
    const Icon = isRailCollapsed ? Chat24Icon : Clear24Icon;

    return (
        <Tooltip title={label} placement="top">
            <StyledFloatingToggle
                variant={isRailCollapsed ? "pill" : "select"}
                color={isRailCollapsed ? "primary" : undefined}
                onClick={() => setIsRailCollapsed(!isRailCollapsed)}
                aria-label={label}
                aria-expanded={!isRailCollapsed}
            >
                <Icon aria-hidden="true" />
            </StyledFloatingToggle>
        </Tooltip>
    );
};
